const { eligibleLines, mixQuantities, volumeTotals, verifyVolumeCart, sale, saleActive } = await import(document.querySelector('gmm-volume-builder').dataset.mathUrl);

class GmmVolumeBuilder extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.abort = new AbortController();
    this.packFlights = new Set();
    this.packRemovals = new Set();
    this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stopFlights = () => {
      this.packFlights.forEach(flight => flight.cancel());
      this.packRemovals.forEach(effect => effect.cancel());
    };
    window.addEventListener('scroll', stopFlights, {capture:true, passive:true, signal:this.abort.signal});
    window.addEventListener('resize', stopFlights, {signal:this.abort.signal});
    this.motionPreference.addEventListener('change', stopFlights, {signal:this.abort.signal});
    document.addEventListener('visibilitychange', stopFlights, {signal:this.abort.signal});
    this.products = [...this.querySelectorAll('[data-product]')].map(card => ({
      id: card.dataset.id, price: Number(card.dataset.price), limit: Number(card.dataset.limit),
      stock: Number(card.dataset.stockLimit),
      name: card.dataset.name, image: card.querySelector('img')?.src, card,
    }));
    this.tiers = [...this.querySelectorAll('[data-tier]')].map(node => ({
      minimum: Number(node.dataset.min), percentage: Number(node.dataset.percentage), title: node.dataset.discountTitle, node,
    })).sort((a, b) => a.minimum - b.minimum);
    this.max = Number(this.dataset.max);
    this.currency = this.dataset.currency;
    this.storageKey = `gmm-volume-v1:${this.dataset.section}:${this.currency}`;
    this.money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.currency }).format(value / 100);
    this.cart = {items: [], currency: this.currency};
    this.cartReady = false;
    this.inventoryReady = false;
    this.inventoryNote = '';
    this.quantities = {};
    this.operation = null;
    this.busy = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      let remaining = this.max;
      for (const p of this.products) {
        const quantity = Math.max(0, Math.min(Math.floor(Number(saved?.quantities?.[p.id]) || 0), remaining));
        this.quantities[p.id] = quantity;
        remaining -= quantity;
      }
      if (saved?.operation?.id && Array.isArray(saved.operation.items)) this.operation = saved.operation;
    } catch { /* Storage is optional; the builder still works without it. */ }
    this.addEventListener('click', event => this.onClick(event), { signal: this.abort.signal });
    this.render();
    this.refreshCart();
    window.addEventListener('pageshow', () => this.refreshCart(), {signal:this.abort.signal});
    window.addEventListener('focus', () => this.refreshCart(), {signal:this.abort.signal});
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refreshCart(); }, {signal:this.abort.signal});
    this.inventoryTimer = setInterval(() => { if (!document.hidden) this.refreshCart(); }, 30000);
  }

  disconnectedCallback() {
    this.packFlights?.forEach(flight => flight.cancel());
    this.packRemovals?.forEach(effect => effect.cancel());
    this.abort?.abort();
    clearInterval(this.inventoryTimer);
    this.initialized = false;
  }

  $(selector) { return this.querySelector(selector); }
  selectedCount() { return this.products.reduce((n,p) => n + (this.quantities[p.id] || 0), 0); }
  displayedQuantities() { return mixQuantities(this.cart, this.ids(), this.operation ? {} : this.quantities); }
  ids() { return this.products.map(p => p.id); }
  totals() {
    const lines = [...eligibleLines(this.cart, this.ids())];
    if (!this.operation) for (const p of this.products) {
      const quantity = this.quantities[p.id] || 0;
      if (quantity) lines.push({variant_id: p.id, quantity, original_price: p.price, original_line_price: p.price * quantity});
    }
    return volumeTotals(lines, this.tiers);
  }
  setCart(cart) {
    this.cart = cart;
    this.cartReady = true;
    for (const p of this.products) {
      const current = eligibleLines(cart, this.ids()).find(l => String(l.variant_id) === p.id);
      if (current) {
        p.price = current.original_price;
        p.card.querySelector('.gmm-pack').textContent = `${current.variant_title} · ${this.money(p.price)}`;
      }
    }
  }
  async refreshCart() {
    if (this.busy) return;
    this.busy = true;
    try {
      if (this.operation) await this.reviewCart();
      else this.setCart(await this.request('cart.js'));
      await this.refreshInventory();
      this.reconcileInventory();
    }
    catch (error) { this.inventoryReady = false; this.inventoryNote = error.message || 'We could not check availability. Please try again.'; }
    finally { this.busy = false; this.render(); }
  }
  stockInCart(product) {
    return this.cart.items.filter(line => String(line.variant_id) === product.id).reduce((n, line) => n + line.quantity, 0);
  }
  remainingStock(product) {
    return product.stock < 0 ? Infinity : Math.max(0, product.stock - this.stockInCart(product));
  }
  stockIssues() {
    return this.products.filter(p => p.stock >= 0 && this.stockInCart(p) > p.stock);
  }
  async refreshInventory() {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    this.abort.signal.addEventListener('abort', cancel, {once:true});
    const timeout = setTimeout(cancel, 10000);
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.section);
      // Shopify varies rendered sections by q; arbitrary cache-busting keys can be ignored.
      url.searchParams.set('q', `gmm-stock-${Date.now()}`);
      const response = await fetch(url, {credentials:'same-origin', cache:'no-store', signal:controller.signal});
      if (!response.ok) throw new Error('Inventory response failed');
      const fragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = [...fragment.querySelectorAll('gmm-volume-builder [data-product]')];
      const stocks = this.products.map(p => {
        const card = cards.find(node => node.dataset.id === p.id);
        const raw = card?.dataset.stockLimit;
        const stock = Number(raw);
        if (raw === undefined || raw === '' || !Number.isInteger(stock) || stock < -1) throw new Error('Incomplete inventory response');
        return stock;
      });
      this.products.forEach((p, index) => { p.stock = stocks[index]; p.card.dataset.stockLimit = String(p.stock); });
      this.inventoryReady = true;
    } catch {
      this.inventoryReady = false;
      throw new Error('We could not refresh stock. Check availability again before adding your bundle.');
    } finally {
      clearTimeout(timeout);
      this.abort.signal.removeEventListener('abort', cancel);
    }
  }
  reconcileInventory() {
    const changed = [];
    let room = Math.max(0, this.max - eligibleLines(this.cart, this.ids()).reduce((n, line) => n + line.quantity, 0));
    if (!this.operation) for (const p of this.products) {
      const before = this.quantities[p.id] || 0;
      const after = Math.min(before, this.remainingStock(p), room);
      room -= after;
      if (after !== before) { this.quantities[p.id] = after; changed.push(p.name); }
    }
    if (changed.length) {
      this.save();
      this.message(`Availability or your cart changed. We adjusted ${changed.join(', ')} in your selection. Review your updated mix and savings before adding.`);
    }
    const issues = this.stockIssues();
    this.inventoryNote = issues.length ? `${issues.map(p => p.name).join(', ')} in your cart exceeds current stock. Remove the extra packs or review your cart before checkout.` : '';
    return changed.length > 0;
  }
  save() {
    try { sessionStorage.setItem(this.storageKey, JSON.stringify({ quantities: this.quantities, operation: this.operation })); } catch { /* Private browsing can disable storage. */ }
  }
  message(text) { this.$('[data-status]').textContent = text; }

  onClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button) || button.disabled || this.busy) return;
    if (button.matches('[data-stock-retry]')) { this.refreshCart(); return; }
    if (button.matches('[data-submit]')) {
      if (!this.selectedCount() && !this.operation) window.location.assign(`${this.dataset.root}cart`);
      else this.submit();
      return;
    }
    if (button.matches('[data-checkout]')) { this.checkout(); return; }
    if (button.matches('[data-reset-qa]')) { this.resetQa(); return; }
    if (button.matches('[data-remove-test]')) { this.removeBundle(); return; }
    if (this.operation) return;
    const id = button.dataset.remove || button.closest('[data-product]')?.dataset.id;
    const p = this.products.find(product => product.id === id);
    if (!p) return;
    const previousTier = this.totals().percentage;
    const quantity = this.quantities[id] || 0;
    const sourceImage = p.card.querySelector('.gmm-product-image img');
    const sourceRect = sourceImage.getBoundingClientRect();
    let removal;
    if (button.dataset.action === 'plus' && this.inventoryReady && this.totals().count < this.max && quantity < this.remainingStock(p)) this.quantities[id] = quantity + 1;
    else if (button.dataset.action === 'minus' || button.dataset.remove) {
      this.packFlights.forEach(flight => flight.cancel());
      const key = button.closest('[data-pack-key]')?.dataset.packKey;
      if (!quantity) { this.removeCartPack(p, key); return; }
      removal = this.removalSource(p, key);
      this.quantities[id] = quantity - 1;
    }
    this.message('');
    this.save();
    const removedSlot = Boolean(button.dataset.remove);
    this.render();
    this.poofPack(removal);
    this.animateSelection(p, (this.quantities[id] || 0) > quantity, previousTier, sourceRect);
    if (removedSlot || (!button.matches('.gmm-product-image') && (quantity === 0 || !this.quantities[id]))) {
      p.card.querySelector(this.displayedQuantities()[id] ? '.gmm-controls [data-action="minus"]' : '.gmm-quick-add').focus({preventScroll:true});
    }
  }

  async removeCartPack(product, key) {
    let removal;
    this.busy = true;
    this.message('');
    this.render();
    try {
      const cart = await this.request('cart.js');
      const line = eligibleLines(cart, this.ids()).find(item => String(item.variant_id) === product.id);
      const updated = line ? await this.request('cart/change.js', { id: line.key, quantity: line.quantity - 1 }) : cart;
      if (line && (mixQuantities(updated, this.ids())[product.id] || 0) < (mixQuantities(cart, this.ids())[product.id] || 0)) {
        removal = this.removalSource(product, key);
      }
      this.setCart(updated);
      if (this.inventoryReady) this.reconcileInventory();
      this.message(`Your cart is updated.`);
    } catch (error) {
      this.message(error.message);
      try { this.setCart(await this.request('cart.js')); }
      catch { this.cartReady = false; }
    } finally {
      this.busy = false;
      this.render();
      this.poofPack(removal);
      product.card.querySelector(this.displayedQuantities()[product.id] ? '.gmm-controls [data-action="minus"]' : '.gmm-quick-add').focus({preventScroll:true});
    }
  }

  render() {
    const totals = this.totals();
    const locked = this.busy || !this.cartReady || Boolean(this.operation);
    const displayed = this.displayedQuantities();
    const selected = this.selectedCount();
    for (const p of this.products) {
      const quantity = displayed[p.id] || 0;
      p.card.classList.toggle('is-selected', quantity > 0);
      p.card.querySelector('[data-quantity]').textContent = quantity;
      p.card.querySelector('[data-action="minus"]').disabled = locked || quantity === 0;
      const remaining = Math.max(0, this.remainingStock(p) - (this.operation ? 0 : this.quantities[p.id] || 0));
      for (const plus of p.card.querySelectorAll('[data-action="plus"]')) plus.disabled = locked || !this.inventoryReady || remaining === 0 || totals.count >= this.max;
      p.card.classList.toggle('is-sold-out', p.stock === 0);
      p.card.querySelector('.gmm-quick-add span').textContent = p.stock === 0 ? 'Sold out' : !this.inventoryReady ? 'Checking stock' : remaining === 0 ? 'All available packs in cart' : 'Add to bundle';
      const availability = p.card.querySelector('[data-availability]');
      availability.textContent = p.stock === 0 ? 'Sold out · try another flavor' : !this.inventoryReady ? '' : remaining === 0 ? 'You have all available packs' : remaining <= 5 ? `${remaining} more ${remaining === 1 ? 'pack' : 'packs'} available` : '';
      availability.hidden = !availability.textContent;
      p.card.querySelector('[data-selected-badge]').textContent = quantity ? `${quantity} in your mix` : '';
      p.card.querySelector('.gmm-quick-add').hidden = quantity > 0;
      p.card.querySelector('.gmm-controls').hidden = quantity === 0;
    }
    this.$('[data-count]').textContent = `${totals.count} / ${Math.max(this.max, totals.count)}`;
    this.$('[data-mobile-count]').textContent = totals.count;
    this.$('[data-mobile-total]').textContent = this.money(totals.total);
    this.$('[data-subtotal]').textContent = this.money(totals.subtotal);
    this.$('[data-saving]').textContent = `−${this.money(totals.saving)}`;
    this.$('[data-total]').textContent = this.money(totals.total);
    this.$('[data-total-label]').textContent = this.operation ? 'GMM total in cart' : 'Estimated GMM total';
    if (this.operation) {
      const actual = eligibleLines(this.cart, this.ids()).reduce((n,l) => n+l.final_line_price,0);
      this.$('[data-total]').textContent = this.money(actual);
      this.$('[data-mobile-total]').textContent = this.money(actual);
      this.$('[data-saving]').textContent = `−${this.money(totals.subtotal-actual)}`;
    }
    const supported = this.currency === 'USD';
    const existing = eligibleLines(this.cart, this.ids()).reduce((n,l) => n+l.quantity,0);
    this.$('[data-existing]').textContent = this.operation ? `${existing} eligible packs in your cart. Totals below use Shopify’s current prices.` : existing ? `${existing} packs already in your cart are shown in your mix.${selected ? ` ${selected} new ${selected === 1 ? 'pack is' : 'packs are'} ready to add.` : ' Add more flavors or review your cart.'}` : 'Mix any flavors. Repeated flavors count too.';
    const next = totals.next;
    const remaining = next ? next.minimum - totals.count : 0;
    const target = next?.minimum || this.tiers.at(-1)?.minimum || 1;
    const ready = supported && this.cartReady;
    const progress = ready ? Math.min(100, totals.count / target * 100) : 0;
    const headline = !this.cartReady ? 'Finding your savings…'
      : !supported ? 'Select USD to see bundle savings'
      : next ? `${remaining === 1 ? 'Just 1 more pack' : `Add ${remaining} packs`} to unlock ${next.percentage}% off`
      : `You've unlocked ${totals.percentage}% off`;
    this.$('[data-progress]').setAttribute('aria-valuenow', String(Math.round(progress)));
    this.$('[data-progress]').setAttribute('aria-valuetext', ready ? `${totals.count} eligible packs. ${next ? `${remaining} more to unlock ${next.percentage}% off.` : 'Top discount unlocked.'}` : 'Savings unavailable');
    this.$('[data-progress-fill]').style.width = `${progress}%`;
    this.$('[data-progress-count]').textContent = ready ? `${totals.count} of ${target} packs${next ? '' : ' · goal reached'}` : '';
    this.$('[data-progress-goal]').textContent = ready ? `${next?.percentage || totals.percentage}% off${next ? ' next' : ' unlocked'}` : '';
    this.$('[data-reward]').textContent = headline;
    this.$('[data-reward]').classList.toggle('is-earned', ready && !next);
    this.$('[data-reward]').classList.toggle('is-close', ready && remaining === 1);
    this.$('[data-mobile-next]').textContent = ready ? next ? `${remaining} more ${remaining === 1 ? 'pack' : 'packs'} unlocks ${next.percentage}% off` : `${totals.percentage}% off unlocked` : '';
    for (const t of this.tiers) {
      t.node.classList.toggle('is-unlocked', ready && totals.count >= t.minimum);
      t.node.classList.toggle('is-current', ready && totals.tier === t);
      t.node.classList.toggle('is-next', ready && next === t);
    }
    this.$('[data-next]').textContent = !supported ? 'This preview supports USD pricing. Select USD to try the bundle.'
      : !this.cartReady ? ''
      : next ? totals.percentage ? `Go from ${totals.percentage}% to ${next.percentage}% off every eligible GMM pack. Mix any flavors.` : 'Your discount applies to every eligible GMM pack. Mix any flavors.'
      : 'Our best bundle discount applies to every eligible GMM pack.';
    this.renderSale(totals, ready);
    const slots = this.$('[data-slots]');
    slots.replaceChildren();
    let index = 0;
    for (const p of this.products) {
      for (let n = 0; n < (displayed[p.id] || 0); n++) {
        const slot = document.createElement('li');
        slot.className = 'gmm-slot is-filled';
        slot.dataset.variant = p.id;
        slot.dataset.packKey = `${p.id}:${n}`;
        slot.classList.toggle('is-in-flight', [...this.packFlights].some(flight => flight.key === slot.dataset.packKey && !flight.mobile));
        const image = document.createElement('img');
        if (p.image) image.src = p.image;
        image.alt = p.name;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = '×';
        remove.dataset.remove = p.id;
        remove.setAttribute('aria-label', `Remove one ${p.name} from bundle`);
        remove.disabled = locked;
        slot.append(image, remove);
        slots.append(slot);
        index++;
      }
    }
    while (index++ < this.max) {
      const empty = document.createElement('li');
      empty.className = 'gmm-slot';
      empty.textContent = '+';
      empty.setAttribute('aria-label', 'Empty bundle slot');
      slots.append(empty);
    }
    const stockBlocked = !this.inventoryReady || this.stockIssues().length > 0;
    this.$('[data-stock-status]').textContent = this.inventoryNote || (!this.inventoryReady ? 'Checking current availability…' : '');
    this.$('[data-stock-retry]').hidden = this.inventoryReady || this.busy;
    this.$('[data-checkout]').disabled = this.busy || stockBlocked;
    this.$('[data-submit]').disabled = locked || !supported || !totals.count || !this.cartReady || (selected > 0 && stockBlocked) || this.dataset.enabled !== 'true';
    this.$('[data-submit]').textContent = this.dataset.enabled !== 'true' ? 'Discount setup pending' : this.busy ? 'Checking your bundle…' : this.operation ? 'Bundle added' : !selected ? 'Review cart' : existing ? `Add ${selected} new ${selected === 1 ? 'pack' : 'packs'} to cart` : 'Add bundle to cart';
    if (!this.cartReady) {
      for (const selector of ['[data-subtotal]', '[data-saving]', '[data-total]', '[data-mobile-total]']) this.$(selector).textContent = '…';
      this.$('[data-existing]').textContent = 'Reading your cart…';
    }
    this.setAttribute('aria-busy', String(this.busy || !this.cartReady));
  }

  renderSale(totals, ready) {
    const active = saleActive();
    for (const node of this.querySelectorAll('[data-month-sale]')) node.hidden = !active;
    if (!active || !ready) return;
    const count = totals.saleCount || 0;
    const remaining = Math.max(0, sale.minimum - count);
    const applied = totals.saleApplied;
    const headline = applied ? 'Your end-of-month offer is unlocked'
      : remaining ? `Pick ${remaining} more ${remaining === 1 ? 'sale pouch' : 'sale pouches'} for the $69 trio`
      : 'Your regular bundle tier saves you more';
    this.$('[data-reward]').textContent = headline;
    this.$('[data-reward]').classList.toggle('is-earned', applied || remaining === 0);
    this.$('[data-reward]').classList.toggle('is-close', remaining === 1);
    const progress = Math.min(100, count / sale.minimum * 100);
    this.$('[data-progress]').setAttribute('aria-valuenow', String(Math.round(progress)));
    this.$('[data-progress]').setAttribute('aria-valuetext', `${count} sale-eligible pouches. ${remaining ? `${remaining} more to qualify.` : 'Offer qualified.'}`);
    this.$('[data-progress-fill]').style.width = `${progress}%`;
    this.$('[data-progress-count]').textContent = `${count} sale-eligible pouches`;
    this.$('[data-progress-goal]').textContent = applied ? '$56.97 off once' : remaining ? '3 pouches for $69' : 'Best offer applied';
    this.$('[data-mobile-next]').textContent = applied ? '$56.97 sale savings' : remaining ? `${remaining} more sale pouches to qualify` : 'Best bundle savings applied';
    this.$('[data-next]').textContent = applied
      ? 'Sale savings apply once per order. Three eligible pouches total $69; additional pouches cost extra. Offers do not stack.'
      : 'Sale flavors: Watermelon, Strawberry Kiwi, Sunburst Citrus and Grape. Mix and match or repeat a favorite. The best qualifying offer applies.';
    if (applied) for (const tier of this.tiers) tier.node.classList.remove('is-current', 'is-next');
  }

  removalSource(product, key) {
    if (this.motionPreference.matches) return;
    const visible = rect => rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.bottom <= innerHeight && rect.left >= 0 && rect.right <= innerWidth;
    const slots = [...this.querySelectorAll('[data-slots] [data-variant]')].filter(slot => slot.dataset.variant === product.id);
    const slot = slots.find(slot => slot.dataset.packKey === key) || slots.at(-1);
    let image = slot?.querySelector('img');
    let rect = image?.getBoundingClientRect();
    if (!rect || !visible(rect)) {
      image = product.card.querySelector('.gmm-product-image img');
      rect = image.getBoundingClientRect();
      if (!visible(rect)) return;
      const scale = Math.min(1, 72 / Math.max(rect.width, rect.height));
      rect = {left:rect.left + rect.width * (1-scale) / 2, top:rect.top + rect.height * (1-scale) / 2, width:rect.width * scale, height:rect.height * scale};
    }
    if (!image.complete || !image.naturalWidth) return;
    return {rect, src:image.currentSrc || image.src, color:getComputedStyle(product.card).getPropertyValue('--flavor').trim() || '#eee7f3'};
  }

  poofPack(source) {
    if (!source || this.motionPreference.matches || document.hidden) return;
    const {rect, src, color} = source;
    const cloud = document.createElement('span');
    cloud.className = 'gmm-removing-pack';
    cloud.setAttribute('aria-hidden', 'true');
    Object.assign(cloud.style, {left:`${rect.left}px`, top:`${rect.top}px`, width:`${rect.width}px`, height:`${rect.height}px`});
    cloud.style.setProperty('--poof-color', color);
    const image = document.createElement('img');
    image.src = src;
    image.alt = '';
    cloud.append(image);
    document.body.append(cloud);
    const animations = [image.animate([
      {transform:'scale(1)', opacity:1},
      {transform:'scale(.88,1.06)', opacity:1, offset:.3},
      {transform:'translateY(-3px) scale(1.12,.9)', opacity:1, offset:.5},
      {transform:'translateY(-6px) scale(.25)', opacity:0},
    ], {duration:260, easing:'cubic-bezier(.3,0,.3,1)', fill:'forwards'})];
    for (let i = 0; i < 7; i++) {
      const puff = document.createElement('span');
      puff.className = 'gmm-poof-particle';
      cloud.append(puff);
      const angle = i * Math.PI * 2 / 7;
      const radius = Math.max(22, Math.min(40, rect.width * .7));
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius - 8;
      animations.push(puff.animate([
        {transform:'translate(-50%,-50%) scale(.2)', opacity:0},
        {transform:`translate(calc(-50% + ${x * .5}px),calc(-50% + ${y * .5}px)) scale(1)`, opacity:.8, offset:.35},
        {transform:`translate(calc(-50% + ${x}px),calc(-50% + ${y}px)) scale(.45)`, opacity:0},
      ], {duration:440, delay:80, easing:'cubic-bezier(.16,.7,.3,1)', fill:'both'}));
    }
    const effect = {cancel:() => {
      if (!this.packRemovals.delete(effect)) return;
      animations.forEach(animation => animation.cancel());
      cloud.remove();
    }};
    this.packRemovals.add(effect);
    Promise.allSettled(animations.map(animation => animation.finished)).then(effect.cancel);
  }

  flyPack(product, sourceRect) {
    const source = product.card.querySelector('.gmm-product-image img');
    const slot = [...this.querySelectorAll('[data-slots] [data-variant]')].filter(node => node.dataset.variant === product.id).at(-1);
    const visible = rect => rect.width > 0 && rect.height > 0 && rect.top >= 0 && rect.bottom <= innerHeight && rect.left >= 0 && rect.right <= innerWidth;
    if (!slot || !source.complete || !source.naturalWidth || !visible(sourceRect)) return;
    const mobile = !visible(slot.getBoundingClientRect());
    const target = mobile ? this.$('.gmm-mobile-summary [data-mobile-count]') : slot.querySelector('img');
    if (!target || !visible(target.getBoundingClientRect())) return;
    const key = slot.dataset.packKey;
    const pack = document.createElement('img');
    pack.src = source.currentSrc || source.src;
    pack.alt = '';
    pack.setAttribute('aria-hidden', 'true');
    pack.className = 'gmm-flying-pack';
    Object.assign(pack.style, {width:`${sourceRect.width}px`, height:`${sourceRect.height}px`, left:`${sourceRect.left}px`, top:`${sourceRect.top}px`});
    document.body.append(pack);
    if (!mobile) slot.classList.add('is-in-flight');
    let frame;
    const currentSlot = () => [...this.querySelectorAll('[data-pack-key]')].find(node => node.dataset.packKey === key);
    const cleanup = () => {
      cancelAnimationFrame(frame);
      pack.remove();
      currentSlot()?.classList.remove('is-in-flight');
      this.packFlights.delete(flight);
    };
    const flight = {key, mobile, cancel:cleanup};
    this.packFlights.add(flight);
    const start = performance.now();
    const origin = {x:sourceRect.left + sourceRect.width / 2, y:sourceRect.top + sourceRect.height / 2};
    const initialTarget = target.getBoundingClientRect();
    const distance = Math.hypot(initialTarget.left - sourceRect.left, initialTarget.top - sourceRect.top);
    const duration = Math.min(820, Math.max(640, 560 + distance * .25));
    const tick = now => {
      const landingSlot = currentSlot();
      const landing = mobile ? target : landingSlot?.querySelector('img');
      if (!landingSlot || !landing?.isConnected) { cleanup(); return; }
      const rect = landing.getBoundingClientRect();
      if (!visible(rect)) { cleanup(); return; }
      const t = Math.min(1, (now - start) / duration);
      const travel = 1 - Math.pow(1 - t, 2.5);
      const end = {x:rect.left + rect.width / 2, y:rect.top + rect.height / 2};
      const lift = Math.min(160, Math.max(65, distance * .22));
      const controlY = Math.max(sourceRect.height / 2 + 8, Math.min(origin.y, end.y) - lift * 2);
      const x = origin.x + (end.x - origin.x) * travel;
      const y = (1-travel) ** 2 * origin.y + 2 * (1-travel) * travel * controlY + travel ** 2 * end.y;
      const endScale = mobile ? Math.min(32 / sourceRect.width, 32 / sourceRect.height) : Math.min(rect.width / sourceRect.width, rect.height / sourceRect.height);
      const scale = 1 + (endScale - 1) * travel;
      const tilt = Math.sin(travel * Math.PI) * (end.x >= origin.x ? -12 : 12);
      pack.style.transform = `translate(${x-origin.x}px, ${y-origin.y}px) rotate(${tilt}deg) scale(${scale})`;
      pack.style.opacity = mobile ? String(1 - Math.max(0, (t-.82)/.18)) : '1';
      if (t < 1) { frame = requestAnimationFrame(tick); return; }
      cleanup();
      const receiver = mobile ? this.$('.gmm-mobile-summary') : landingSlot;
      receiver.getAnimations().forEach(animation => animation.cancel());
      receiver.animate(mobile ? [
        {transform:'translateY(0)'}, {transform:'translateY(-4px)',offset:.4}, {transform:'translateY(0)'},
      ] : [
        {transform:'scale(1)'}, {transform:'scale(1.08,.92)',offset:.3},
        {transform:'scale(.98,1.04)',offset:.65}, {transform:'scale(1)'},
      ], {duration:280, easing:'cubic-bezier(.2,.7,.3,1)'});
    };
    frame = requestAnimationFrame(tick);
  }

  animateSelection(product, added, previousTier, sourceRect) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animate = (node, frames, duration = 360) => {
      if (!node) return;
      node.getAnimations().forEach(animation => animation.cancel());
      node.animate(frames, {duration, easing: 'cubic-bezier(.2,.8,.2,1)'});
    };
    animate(product.card.querySelector('.gmm-product-image img'), [
      {transform: 'translateY(0) rotate(0)'},
      {transform: `translateY(${added ? -7 : 3}px) rotate(${added ? -2 : 1}deg)`},
      {transform: 'translateY(0) rotate(0)'},
    ]);
    if (added) {
      this.flyPack(product, sourceRect);
    }
    animate(this.$('[data-total]'), [{transform: 'translateY(3px)', opacity: .5}, {transform: 'translateY(0)', opacity: 1}], 240);
    if (this.totals().percentage > previousTier) {
      animate(this.$('[data-reward]'), [{transform: 'scale(.97)'}, {transform: 'scale(1.035)'}, {transform: 'scale(1)'}], 480);
      animate(this.$('.gmm-mobile-summary'), [{transform: 'translateY(0)'}, {transform: 'translateY(-5px)'}, {transform: 'translateY(0)'}]);
    }
  }

  async request(path, body) {
    const response = await fetch(`${this.dataset.root}${path}`, {
      method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin', cache: 'no-store', ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : 'The cart could not be updated. Review your cart before trying again.');
    return data;
  }

  async submit() {
    if (this.busy || this.operation) return;
    if (!this.selectedCount() || this.currency !== 'USD' || this.dataset.enabled !== 'true' || !this.cartReady) return;
    this.busy = true;
    this.message('');
    this.render();
    try {
      // Read first so a failed connection cannot start a blind cart mutation.
      const before = await this.request('cart.js');
      if (before.currency !== this.currency) throw new Error('Your currency changed. Reload this page before adding your bundle.');
      this.setCart(before);
      await this.refreshInventory();
      if (this.reconcileInventory()) return;
      if (this.stockIssues().length) throw new Error(this.inventoryNote);
      this.operation = { id: crypto.randomUUID(), currency: this.currency,
        items: this.products.filter(p => this.quantities[p.id]).map(p => ({ id: p.id, quantity: this.quantities[p.id] })) };
      this.save();
      await this.request('cart/add.js', { items: this.operation.items.map(item => ({ ...item, properties: { _gmm_bundle_id: this.operation.id } })) });
      await this.reviewCart();
    } catch (error) {
      this.message(error.message);
      // A failed add can still have changed the cart. Never automatically replay it.
      if (this.operation) await this.reviewCart();
      try { await this.refreshInventory(); this.reconcileInventory(); }
      catch (stockError) { this.inventoryNote = stockError.message; }
    } finally {
      this.busy = false;
      this.render();
    }
  }

  async reviewCart() {
    const result = this.$('[data-result]');
    result.hidden = false;
    this.$('[data-checkout]').hidden = true;
    this.$('[data-remove-test]').hidden = true;
    try {
      const cart = await this.request('cart.js');
      this.setCart(cart);
      if (this.dataset.qa === 'true') this.$('[data-qa-attributes]').textContent = `Cart attribute keys: ${Object.keys(cart.attributes || {}).join(', ') || 'none'}`;
      const check = verifyVolumeCart(cart, this.ids(), this.tiers, this.currency);
      const addedLines = cart.items.filter(line => line.properties?._gmm_bundle_id === this.operation?.id);
      const additionComplete = this.operation?.confirmed || (addedLines.length > 0 && this.operation.items.every(item => addedLines.filter(l => String(l.variant_id) === item.id).reduce((n,l) => n+l.quantity,0) === item.quantity));
      if (additionComplete && this.operation) this.operation.confirmed = true;
      check.matches = check.matches && Boolean(additionComplete);
      if (!addedLines.length) {
        this.operation = null;
        this.save();
        this.$('[data-result-text]').textContent = 'Your selections are ready to add.';
      } else {
        this.$('[data-result-text]').textContent = check.matches
          ? `${check.count} GMM packs · ${check.saleApplied ? "end-of-month sale" : `${check.percentage}% off`} · ${this.money(check.total)}. Cart total: ${this.money(cart.total_price)} before shipping and taxes.`
          : `Your cart pricing has changed. Review your cart to see the current discounts and total before checking out.`;
        this.$('[data-checkout]').hidden = !check.matches;
        this.$('[data-remove-test]').hidden = this.dataset.review !== 'true';
      }
      if (addedLines.length) {
        for (const p of this.products) {
          this.quantities[p.id] = addedLines.filter(l => String(l.variant_id) === p.id).reduce((n,l) => n+l.quantity,0);
        }
        this.save();
      }
      this.render();
      return Boolean(this.operation) && check.matches;
    } catch {
      this.cartReady = false;
      this.$('[data-result-text]').textContent = 'We could not verify the cart. Review your cart before adding anything again. Reload this page to retry the check.';
      return false;
    }
  }

  async checkout() {
    if (!this.operation || this.busy) return;
    this.busy = true;
    this.render();
    this.$('[data-checkout]').disabled = true;
    try {
      const verified = await this.reviewCart();
      await this.refreshInventory();
      this.reconcileInventory();
      if (verified && !this.stockIssues().length) window.location.assign(`${this.dataset.root}checkout`);
    } catch (error) {
      this.inventoryNote = error.message;
    } finally {
      this.busy = false;
      this.$('[data-checkout]').disabled = false;
      this.render();
    }
  }

  async resetQa() {
    if (this.dataset.qa !== 'true' || this.busy) return;
    this.busy = true; this.render();
    try {
      const cart = await this.request('cart.js');
      if (cart.items.some(l => !this.ids().includes(String(l.variant_id)))) throw new Error('Reset is limited to carts containing only these QA products.');
      const attributes = Object.fromEntries(Object.keys(cart.attributes || {}).filter(k => /rebuy/i.test(k) || ['_attribution', '_source', '_barId'].includes(k)).map(k => [k, '']));
      await this.request('cart/update.js', {attributes, discount:''});
      await this.reviewCart();
    } catch (error) { this.message(error.message); }
    finally { this.busy = false; this.render(); }
  }

  async removeBundle() {
    if (!this.operation || this.busy) return;
    this.busy = true;
    this.render();
    this.$('[data-remove-test]').disabled = true;
    try {
      const cart = await this.request('cart.js');
      const updates = Object.fromEntries(cart.items.filter(line => line.properties?._gmm_bundle_id === this.operation.id).map(line => [line.key, 0]));
      if (Object.keys(updates).length) await this.request('cart/update.js', { updates });
      await this.reviewCart();
      if (!this.operation) this.message('Preview bundle removed. Other cart items were kept.');
    } catch (error) { this.message(error.message); }
    finally { this.busy = false; this.$('[data-remove-test]').disabled = false; this.render(); }
  }
}
if (!customElements.get('gmm-volume-builder')) customElements.define('gmm-volume-builder', GmmVolumeBuilder);
